import React, { useState } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { PaymentData } from '../types';
import { Investment } from '../../../types';
import { Calendar, DollarSign, CreditCard, Plus, Minus, Trash2, Edit, Save } from 'lucide-react';
import { useCalculator } from '../../../context/CalculatorContext';

interface DownPaymentScheduleTabProps {
  paymentData: PaymentData;
  investment: Investment;
}

const DownPaymentScheduleTab: React.FC<DownPaymentScheduleTabProps> = ({ paymentData, investment }) => {
  const { updateInvestment } = useCalculator();
  const [isEditing, setIsEditing] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Array<{
    id: string;
    amount: number;
    date: string;
    description?: string;
  }>>([]);
  const [selectedMonthlyDay, setSelectedMonthlyDay] = useState(15); // Default to 15th of each month

  const totalInvestment = investment.initialCertificates * investment.certificateBasePrice;
  const downPaymentAmount = totalInvestment * (investment.downPaymentPercentage || 30) / 100;

  // Initialize editing schedule
  React.useEffect(() => {
    if (investment.enableCustomDownPaymentSchedule && investment.customDownPaymentSchedule) {
      setEditingSchedule([...investment.customDownPaymentSchedule]);
    } else {
      // Create default schedule based on number of installments
      const installments = investment.downPaymentInstallments || 1;
      const amountPerInstallment = downPaymentAmount / installments;
      const defaultSchedule = Array.from({ length: installments }, (_, index) => {
        const date = new Date();
        date.setMonth(date.getMonth() + index);
        return {
          id: `payment-${index + 1}`,
          amount: amountPerInstallment,
          date: date.toISOString().split('T')[0],
          description: `Pago ${index + 1} de ${installments}`
        };
      });
      setEditingSchedule(defaultSchedule);
    }
  }, [investment.enableCustomDownPaymentSchedule, investment.downPaymentInstallments, downPaymentAmount]);

  const handleToggleCustomSchedule = () => {
    const newValue = !investment.enableCustomDownPaymentSchedule;
    updateInvestment({ enableCustomDownPaymentSchedule: newValue });
    
    if (newValue) {
      // Enable custom schedule
      setIsEditing(true);
    } else {
      // Disable custom schedule
      setIsEditing(false);
      updateInvestment({ customDownPaymentSchedule: [] });
    }
  };

  const handleInstallmentsChange = (installments: number) => {
    updateInvestment({ downPaymentInstallments: installments });
    
    // Update editing schedule
    const amountPerInstallment = downPaymentAmount / installments;
    const newSchedule = Array.from({ length: installments }, (_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() + index);
      return {
        id: `payment-${index + 1}`,
        amount: amountPerInstallment,
        date: date.toISOString().split('T')[0],
        description: `Pago ${index + 1} de ${installments}`
      };
    });
    setEditingSchedule(newSchedule);
  };

  const handleApplyMonthlyDay = () => {
    const newSchedule = editingSchedule.map((payment, index) => {
      const baseDate = new Date();
      baseDate.setMonth(baseDate.getMonth() + index);
      baseDate.setDate(selectedMonthlyDay);
      
      return {
        ...payment,
        date: baseDate.toISOString().split('T')[0]
      };
    });
    setEditingSchedule(newSchedule);
    updateInvestment({ customDownPaymentSchedule: newSchedule });
  };

  const handleAddPayment = () => {
    const newPayment = {
      id: `payment-${Date.now()}`,
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      description: `Pago adicional`
    };
    setEditingSchedule([...editingSchedule, newPayment]);
  };

  const handleRemovePayment = (id: string) => {
    setEditingSchedule(editingSchedule.filter(payment => payment.id !== id));
  };

  const handleUpdatePayment = (id: string, field: string, value: any) => {
    setEditingSchedule(editingSchedule.map(payment => 
      payment.id === id ? { ...payment, [field]: value } : payment
    ));
  };

  const handleSaveSchedule = () => {
    updateInvestment({ customDownPaymentSchedule: editingSchedule });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset to saved schedule
    if (investment.customDownPaymentSchedule) {
      setEditingSchedule([...investment.customDownPaymentSchedule]);
    }
  };

  const totalScheduledAmount = editingSchedule.reduce((sum, payment) => sum + payment.amount, 0);
  const remainingAmount = downPaymentAmount - totalScheduledAmount;

  return (
    <div className="space-y-8">
      {/* Down Payment Configuration */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Configuración del Enganche</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
            <div className="text-sm text-blue-600 mb-1">Inversión Total</div>
            <div className="text-xl font-bold text-blue-700">
              {formatCurrency(totalInvestment, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
            <div className="text-sm text-green-600 mb-1">Enganche Requerido ({investment.downPaymentPercentage || 30}%)</div>
            <div className="text-xl font-bold text-green-700">
              {formatCurrency(downPaymentAmount, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="text-sm text-purple-600 mb-1">A Financiar</div>
            <div className="text-xl font-bold text-purple-700">
              {formatCurrency(paymentData.totalFinanced, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
            <div className="text-sm text-orange-600 mb-1">Mensualidad</div>
            <div className="text-xl font-bold text-orange-700">
              {formatCurrency(paymentData.totalFinanced / 48, investment.currencyFormat)}
            </div>
          </div>
        </div>

        {/* Custom Schedule Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-6">
          <div>
            <h4 className="font-medium text-gray-800">Cronograma Personalizado de Enganche</h4>
            <p className="text-sm text-gray-600">Divide el enganche en múltiples pagos con fechas personalizadas</p>
          </div>
          <button
            onClick={handleToggleCustomSchedule}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              investment.enableCustomDownPaymentSchedule ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                investment.enableCustomDownPaymentSchedule ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Number of Installments (when not using custom schedule) */}
        {!investment.enableCustomDownPaymentSchedule && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Número de Pagos para el Enganche
            </label>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleInstallmentsChange(Math.max(1, (investment.downPaymentInstallments || 1) - 1))}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={(investment.downPaymentInstallments || 1) <= 1}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              
              <div className="flex-1 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {investment.downPaymentInstallments || 1}
                </div>
                <div className="text-sm text-gray-600">
                  {(investment.downPaymentInstallments || 1) === 1 ? 'pago único' : 'pagos'}
                </div>
              </div>
              
              <button
                onClick={() => handleInstallmentsChange(Math.min(3, (investment.downPaymentInstallments || 1) + 1))}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={(investment.downPaymentInstallments || 1) >= 3}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[1, 2, 3].map((installments) => (
                <button
                  key={installments}
                  onClick={() => handleInstallmentsChange(installments)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    (investment.downPaymentInstallments || 1) === installments
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {installments}
                </button>
              ))}
            </div>
            
            {(investment.downPaymentInstallments || 1) > 1 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-700">
                  <strong>Monto por pago:</strong> {formatCurrency(downPaymentAmount / (investment.downPaymentInstallments || 1), investment.currencyFormat)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom Payment Schedule Editor */}
      {investment.enableCustomDownPaymentSchedule && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Edit className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Editor de Cronograma Personalizado</h3>
            </div>
            
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveSchedule}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Guardar
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
              )}
            </div>
          </div>

          {/* Balance Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
              <div className="text-sm text-green-600 mb-1">Enganche Requerido</div>
              <div className="text-xl font-bold text-green-700">
                {formatCurrency(downPaymentAmount, investment.currencyFormat)}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
              <div className="text-sm text-blue-600 mb-1">Total Programado</div>
              <div className="text-xl font-bold text-blue-700">
                {formatCurrency(totalScheduledAmount, investment.currencyFormat)}
              </div>
            </div>
            
            <div className={`bg-gradient-to-br p-4 rounded-xl ${
              Math.abs(remainingAmount) < 100 
                ? 'from-green-50 to-green-100' 
                : 'from-red-50 to-red-100'
            }`}>
              <div className={`text-sm mb-1 ${
                Math.abs(remainingAmount) < 100 ? 'text-green-600' : 'text-red-600'
              }`}>
                {remainingAmount > 100 ? 'Faltante' : remainingAmount < -100 ? 'Excedente' : 'Balance'}
              </div>
              <div className={`text-xl font-bold ${
                Math.abs(remainingAmount) < 100 ? 'text-green-700' : 'text-red-700'
              }`}>
                {formatCurrency(Math.abs(remainingAmount), investment.currencyFormat)}
              </div>
            </div>
          </div>

          {/* Payment Schedule Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pago #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  {isEditing && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {editingSchedule.map((payment, index) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {isEditing ? (
                        <input
                          type="date"
                          value={payment.date}
                          onChange={(e) => handleUpdatePayment(payment.id, 'date', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="text-gray-500">
                          {new Date(payment.date).toLocaleDateString('es-MX')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          step="1000"
                          value={payment.amount}
                          onChange={(e) => handleUpdatePayment(payment.id, 'amount', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-right"
                        />
                      ) : (
                        <span className="font-medium text-gray-900">
                          {formatCurrency(payment.amount, investment.currencyFormat)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isEditing ? (
                        <input
                          type="text"
                          value={payment.description || ''}
                          onChange={(e) => handleUpdatePayment(payment.id, 'description', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Descripción del pago"
                        />
                      ) : (
                        <span className="text-gray-500">
                          {payment.description || 'Pago de enganche'}
                        </span>
                      )}
                    </td>
                    {isEditing && (
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleRemovePayment(payment.id)}
                          className="p-1 text-red-600 hover:text-red-800 transition-colors"
                          disabled={editingSchedule.length <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                
                {/* Total Row */}
                <tr className={`font-bold border-t-2 ${
                  Math.abs(remainingAmount) < 100 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-700">
                    TOTAL PROGRAMADO
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-bold ${
                    Math.abs(remainingAmount) < 100 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {formatCurrency(totalScheduledAmount, investment.currencyFormat)}
                  </td>
                  <td className={`px-4 py-3 text-sm font-bold ${
                    Math.abs(remainingAmount) < 100 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {Math.abs(remainingAmount) < 100 ? '✓ Completo' : 
                     remainingAmount > 100 ? `Falta: ${formatCurrency(remainingAmount, investment.currencyFormat)}` :
                     `Excede: ${formatCurrency(Math.abs(remainingAmount), investment.currencyFormat)}`}
                  </td>
                  {isEditing && <td></td>}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Add Payment Button */}
          {isEditing && (
            <div className="flex justify-center">
              <button
                onClick={handleAddPayment}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Agregar Pago
              </button>
            </div>
          )}
        </div>
      )}
        {/* Monthly Day Selector */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-3">Configuración de Fechas</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Día del Mes para Todos los Pagos
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={selectedMonthlyDay}
                  onChange={(e) => setSelectedMonthlyDay(parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>
                      Día {day}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleApplyMonthlyDay}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Aplicar
                </button>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Selecciona el día del mes que se repetirá para todos los pagos
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                Días Comunes
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 5, 15, 30].map(day => (
                  <button
                    key={day}
                    onClick={() => {
                      setSelectedMonthlyDay(day);
                      // Auto-apply when clicking preset
                      const newSchedule = editingSchedule.map((payment, index) => {
                        const baseDate = new Date();
                        baseDate.setMonth(baseDate.getMonth() + index);
                        baseDate.setDate(day);
                        
                        return {
                          ...payment,
                          date: baseDate.toISOString().split('T')[0]
                        };
                      });
                      setEditingSchedule(newSchedule);
                      updateInvestment({ customDownPaymentSchedule: newSchedule });
                    }}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      selectedMonthlyDay === day
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>


      {/* Quick Setup Options */}
      {!investment.enableCustomDownPaymentSchedule && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Opciones de Pago Rápido</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => handleInstallmentsChange(1)}
              className={`p-4 rounded-xl border-2 transition-all ${
                (investment.downPaymentInstallments || 1) === 1
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-800 mb-1">1</div>
                <div className="text-sm text-gray-600 mb-2">Pago Único</div>
                <div className="text-lg font-bold text-green-600">
                  {formatCurrency(downPaymentAmount, investment.currencyFormat)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Inmediato</div>
              </div>
            </button>
            
            <button
              onClick={() => handleInstallmentsChange(2)}
              className={`p-4 rounded-xl border-2 transition-all ${
                (investment.downPaymentInstallments || 1) === 2
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-800 mb-1">2</div>
                <div className="text-sm text-gray-600 mb-2">2 Pagos</div>
                <div className="text-lg font-bold text-blue-600">
                  {formatCurrency(downPaymentAmount / 2, investment.currencyFormat)}
                </div>
                <div className="text-xs text-gray-500 mt-1">c/u en fechas personalizadas</div>
              </div>
            </button>
            
            <button
              onClick={() => handleInstallmentsChange(3)}
              className={`p-4 rounded-xl border-2 transition-all ${
                (investment.downPaymentInstallments || 1) === 3
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-800 mb-1">3</div>
                <div className="text-sm text-gray-600 mb-2">3 Pagos</div>
                <div className="text-lg font-bold text-purple-600">
                  {formatCurrency(downPaymentAmount / 3, investment.currencyFormat)}
                </div>
                <div className="text-xs text-gray-500 mt-1">c/u en fechas personalizadas</div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Standard Down Payment Display */}
      {!investment.enableCustomDownPaymentSchedule && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Cronograma de Enganche</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pago #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Estimada
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {editingSchedule.map((payment, index) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input
                        type="date"
                        value={payment.date}
                        onChange={(e) => {
                          const newSchedule = editingSchedule.map(p => 
                            p.id === payment.id ? { ...p, date: e.target.value } : p
                          );
                          setEditingSchedule(newSchedule);
                          updateInvestment({ customDownPaymentSchedule: newSchedule });
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatCurrency(payment.amount, investment.currencyFormat)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {payment.description || 'Pago de enganche'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-xs text-gray-500">
                        {new Date(payment.date).toLocaleDateString('es-MX')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-green-50">
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm font-medium text-green-700">
                    Total Enganche
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-bold text-green-700">
                    {formatCurrency(totalScheduledAmount, investment.currencyFormat)}
                  </td>
                  <td className="px-6 py-4 text-sm text-green-700">
                    {editingSchedule.length} {editingSchedule.length === 1 ? 'pago' : 'pagos'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Payment Instructions */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Instrucciones de Pago</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Métodos de Pago Aceptados</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Transferencia bancaria</li>
                <li>• Depósito en cuenta</li>
                <li>• Pago con tarjeta de crédito/débito</li>
                <li>• Cheque certificado</li>
              </ul>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Datos Bancarios</h4>
              <div className="text-sm text-green-700 space-y-3">
                <div>
                  <div className="font-bold text-green-800 mb-1">FIDECOMISO 697 - SOLO TRANSFERENCIA</div>
                  <div><strong>Banco:</strong> INSTITUCIÓN DE BANCA MÚLTIPLE, GRUPO FINANCIERO VE POR MÁS</div>
                  <div><strong>No. de Cuenta:</strong> 0000070758</div>
                  <div><strong>CLABE:</strong> 113180000007017586</div>
                </div>
                <div>
                  <div className="font-bold text-green-800 mb-1">BBVA MÉXICO - TRANSFERENCIA Y EFECTIVO</div>
                  <div><strong>No. de Cuenta:</strong> 0118891737</div>
                  <div><strong>CLABE:</strong> 012910001188917370</div>
                </div>
                <div>
                  <div className="font-bold text-green-800 mb-1">SANTANDER - TRANSFERENCIA Y EFECTIVO</div>
                  <div><strong>No. de Cuenta:</strong> 65 50166596</div>
                  <div><strong>CLABE:</strong> 646017230197572477</div>
                </div>
                <div>
                  <div className="font-bold text-green-800 mb-1">CUENTAS EN DÓLARES - CITRUS PATRIMONIAL</div>
                  <div><strong>Banco:</strong> JP MORGAN CHASE BANK N.A.</div>
                  <div><strong>No. de Cuenta:</strong> 336794722</div>
                  <div><strong>ABA (ACH):</strong> 111000614</div>
                  <div><strong>Routing No. (Wire Transfer):</strong> 21000021</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-2">Importante</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Los pagos deben realizarse en las fechas programadas</li>
                <li>• Enviar comprobante de pago por email</li>
                <li>• Conservar todos los comprobantes</li>
                <li>• Contactar al ejecutivo ante cualquier duda</li>
              </ul>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-2">Contacto</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Los pagos deben realizarse puntualmente para mantener el contrato vigente</li>
                <li>• Enviar comprobante a inversiones@citruspatrimonial.mx</li>
                <li>• Para efectivo usar solo BBVA o Santander</li>
                <li>• Transferencias internacionales usar cuenta JP Morgan</li>
                <li>• Pago con tarjeta disponible 24/7 en el link proporcionado</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownPaymentScheduleTab;